<template lang="pug">
div(style="width:100%; height:100%")
  .white--bg(:id="container" style="position:absolute;width:100%; height:100%")
  .map-nodata(v-if="noData" style="width:100%; height:100%; background:hsla(0,0%,100%,0.7); position:absolute;;display:flex;justify-items:center;align-items:center")
    div(style="text-align:center; width:100%;font-size:30px") No data available
</template>

<script>
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import config from "@/config";
import U from "map-gl-utils/noflow/index";
import { getPerms, authHeaders, logEvent } from "@/api";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { tilesetForIndicator } from "@/utils/utils";
import syncMap from "mapbox-gl-sync-move";
// import { queryMbtiles } from "query-remote-tiles";
import queryRemoteTiles from "@/queryRemoteTiles";
import { addIndicatorLayers } from "./mapLayer/indicator";
import { addSchoolOverlays, updateSchoolOverlays } from "./mapLayer/schools";
import {
  addBoundaryOverlays,
  updateBoundaryOverlays,
} from "./mapLayer/boundaryOverlays";
import { env } from "@/getEnv";
import { centroid } from "@turf/centroid";
import {
  isMapboxURL,
  transformMapboxUrl,
} from "maplibregl-mapbox-request-transformer";
window.U = U;
export default {
  props: [
    "boundaryTypeYear",
    "indicator",
    "container",
    "overlays",
    "settingIsochrone",
    "view3D",
    "showNavControl",
  ],
  data: () => ({
    prevSource: null,
    noData: false,
    navControl: null,
  }),
  created() {
    window[this.container === "map2" ? "Mapping2" : "Mapping"] = this;
  },
  async mounted() {
    // debugger;
    if (this.container === "map") window.Mapping = this;
    if (this.container === "map2") window.Mapping2 = this;
    maplibregl.accessToken =
      "pk.eyJ1IjoiYXVvIiwiYSI6ImNtN3NydjNueTFxY3MybHE1MmVwYzR4MDEifQ.YvtxNp983yKuB830tucm0g"; // provided by AUO
    const map = new maplibregl.Map({
      container: this.container,
      center:
        this.container === "map2" ? window.map.getCenter() : [144.96, -37.81],
      zoom: this.container === "map2" ? window.map.getZoom() : 11,
      customAttribution: [
        "Australian Urban Observatory © <a href='http://cur.org.au/research-programs/healthy-liveable-cities-group/' class='primary'>RMIT 2025</a>",
      ],
      style: env.offline
        ? { version: 8, layers: [], sources: {} }
        : "mapbox://styles/mapbox/light-v9",

      transformRequest: (url, resourceType) => {
        if (isMapboxURL(url)) {
          return transformMapboxUrl(url, resourceType, maplibregl.accessToken);
        }
        if (url.match(/maptiler|mapbox/)) {
          return {
            url,
          };
        } else {
          return {
            url,
            ...this.authHeaders,
          };
        }
      },
    });
    // add nav control
    if (this.showNavControl) {
      this.navControl = new maplibregl.NavigationControl();
      map.addControl(this.navControl);
    }

    this.map = map;
    window[this.container] = map;

    U.init(map, maplibregl);
    map.U.onLoad(async () => {
      window.eventBus.emit("map:ready", null, { container: this.container });
      if (this.container === "map2") {
        // debugger;
        syncMap(window.map, window.map2);
      }

      await this.switchLayer();
      addSchoolOverlays({ map });
      await addBoundaryOverlays({ map });
      map.U.clickLayer(
        [`boundary-3d-fill`, `boundary-fill`],
        this.clickBoundaryHandler.bind(this)
      );

      if (this.container === "map") {
        // map.addControl(
        const geocoder = new MapboxGeocoder({
          accessToken: maplibregl.accessToken,
          mapboxgl: maplibregl,
          marker: true,
          clearOnBlur: true,
          types: "region,postcode,district,place,locality,neighborhood,address",
          countries: "au",
          placeholder: "Address search",
        });
        document
          .getElementById("geocoder-container")
          .appendChild(geocoder.onAdd(map));
        geocoder.on("result", (result) => {
          const { center } = result.result;
        });
        geocoder.on("loading", () => {
          const isAddress = geocoder.inputString.match(/\d/i);
          geocoder.options.types =
            "region,postcode,district,place,locality,neighborhood" +
            (isAddress ? ",address" : "");
        });
        window.geocoder = geocoder;
      } else {
        if (window.Mapping.lastClick) {
          map.once("idle", () => {
            this.clickMap(window.Mapping.lastClick);
          });
        }
      }

      window.eventBus.on("click-map", (e) => this.clickMap(e));
    });
    map.on("zoom", () => {
      window.eventBus.emit("map:zoom", null, map.getZoom());
    });

    map.on("move", (e) => {
      if (map.getBearing() !== 0 && !this.view3D && e.source !== "toggle3D") {
        map.setBearing(0);
      }
    });

    map.addControl(new maplibregl.ScaleControl({ maxWidth: 300 }));
  },
  methods: {
    // init(map) {
    //   this.switchLayer({
    //     boundaryType: config.firstBoundaryType,
    //     indicator: config.firstIndicator,
    //   });
    // },
    async addBoundaryTypeYearLayers(options) {
      const permFilter = await this.permFilterForBoundaryType(
        options.boundaryTypeYear
      );
      try {
        await addIndicatorLayers({ ...options, permFilter });
        this.noData = false;
      } catch (e) {
        console.log("Unable to add layer", options, e);
        this.noData = true;
      }
    },
    async permFilterForBoundaryType(boundaryTypeYear) {
      const boundaryType = boundaryTypeYear.replace(/_\d+$/, "");
      const perms = await getPerms();
      return perms[boundaryType] || true;
    },
    async switchLayer() {
      const map = this.map;
      if (this.mouseHandler) {
        map.off("mousemove", this.mouseHandler);
        // map.off("click", "boundary-fill", this.clickHandler);
        this.mouseHandler = undefined;
        // this.clickHandler = undefined;
      }
      if (this.prevSource) {
        map.U.removeSource(this.prevSource);
      }

      this.oldFeature = null;
      this.prevSource = this.currentSource;
      this.authHeaders = await authHeaders();
      await this.addBoundaryTypeYearLayers({
        map: map,
        boundaryTypeYear: this.boundaryTypeYear,
        indicator: this.indicator,
        sourceId: this.currentSource,
        tileset: this.tileset,
        view3D: this.view3D,
      });
      if (this.lastClick) {
        this.map.once("sourcedata", () => {
          this.clickMap(this.lastClick);
        });
      }

      this.mouseHandler = (e) => {
        if (!map.getStyle()?.layers.find((l) => l.id === "boundary-fill")) {
          return;
        }
        if (this.settingIsochrone) {
          return;
        }
        const [feature] = map.queryRenderedFeatures(e.point, {
          layers: ["boundary-3d-fill", "boundary-fill"],
        });
        if (this.oldFeature) {
          map.setFeatureState(this.oldFeature, { hover: false });
          this.oldFeature = null;
        }
        if (feature) {
          map.getCanvas().style.cursor = "pointer";
          if (feature.id) {
            map.setFeatureState(feature, { hover: true });
            this.oldFeature = feature;
          }
        } else {
          map.getCanvas().style.cursor = "";
        }
      };
      map.on("mousemove", this.mouseHandler);
    },
    clickBoundaryHandler(e) {
      if (this.settingIsochrone) {
        return;
      }
      // send event so other map can be clicked at same point.
      window.eventBus.emit("click-map", null, {
        ...e,
        sourceMapContainer: this.container,
      });
      const f = e.features[0];

      this.$emit("select-boundary", f.properties);

      let id = `lga:${f.properties.lga}`;
      if (f.properties.sa1) {
        id = `sa1:${f.properties.sa1}`;
      } else if (f.properties.ssc) {
        id = `ssc:${f.properties.ssc}`;
      }
      logEvent("boundary-select", id);
    },
    setSelectedBoundary(feature) {
      if (
        this.clickedFeature?.id &&
        this.map.getStyle().sources[this.clickedFeature.source]
      ) {
        this.map.setFeatureState(this.clickedFeature, { clicked: false });
      }
      this.clickedFeature = feature;

      if (this.clickedFeature.id) {
        this.map.setFeatureState(this.clickedFeature, { clicked: true });
      }
      this.map.setFilter("boundary-3d-fill-clicked", [
        "==",
        ["id"],
        feature.id,
      ]);
    },
    async clickMap(e) {
      this.lastClick = e;
      const tilesUrl = this.map.getStyle().sources[this.currentSource].tiles[0];
      const source = this.currentSource;
      let res = e;
      // if we clicked this map, we already have the feature we want
      // need to be careful in the case of two 3D maps
      const queryLngLat = centroid(res.features[0]).geometry.coordinates;
      if (e.sourceMapContainer !== this.container) {
        res = await queryRemoteTiles(tilesUrl, queryLngLat, 11, this.tileset);
      }
      const selectedBoundary = res.features[0];
      if (!selectedBoundary) {
        console.log("no boundary in clickMap");
        return;
      }
      this.$emit("select-boundary", selectedBoundary.properties);
      this.setSelectedBoundary({
        ...selectedBoundary,
        source,
        sourceLayer: this.indicator,
      });
    },
    updateView3D() {
      // if (this.container !== "map2") return;
      if (this.view3D) {
        if (this.container !== "map2") {
          // the map sync plugin will cause map2 to follow map1. if we also try to drive it, there's conflict
          this.map.setMaxPitch(60);
          this.map.easeTo({
            pitch: 60,
            duration: 2000,
            source: "toggle3D",
            bearing: 0,
          });
          this.map.once("moveend", () => {
            this.map.setMinPitch(60);
            window.Mapping2?.map.setMinPitch(60);
          });
        }
        this.map.U.addFillExtrusionLayer("buildings-3d", "composite", {
          sourceLayer: "building",
          "fill-extrusion-color": "#aaa",
          "fill-extrusion-height": ["get", "height"],
          "fill-extrusion-base": ["get", "min_height"],
          "fill-extrusion-opacity": 0.6,
        });
      } else {
        if (this.container !== "map2") {
          this.map.setMinPitch(0);
          this.map.easeTo(
            { pitch: 0, bearing: 0, duration: 2000 },
            { source: "toggle3D" }
          );
        }
        this.map.once("moveend", () => {
          this.map.setMinPitch(0);
          window.Mapping2?.map.setMinPitch(0);
        });
      }
      this.switchLayer();
      // if (this.map.getLayer('boundary-border')) {
      //   this.map.U.toggle('boundary-border', !this.view3D)
      // }
    },
  },
  computed: {
    currentSource() {
      return `${this.boundaryTypeYear}${this.tileset}`;
    },
    tileset() {
      return tilesetForIndicator(this.indicator);
    },
  },
  watch: {
    currentSource() {
      this.switchLayer();
    },
    indicator() {
      this.switchLayer();
    },
    overlays: {
      deep: true,
      handler() {
        updateSchoolOverlays({ map: this.map, filter: this.overlays.schools });
        updateBoundaryOverlays({ map: this.map, filter: this.overlays });
      },
    },
    settingIsochrone() {
      if (this.settingIsochrone) {
        this.map.getCanvas().style.cursor = "crosshair";
      } else {
        this.map.getCanvas().style.cursor = "";
      }
    },
    view3D() {
      this.updateView3D();
    },
    showNavControl() {
      if (this.showNavControl) {
        this.navControl = new maplibregl.NavigationControl();
        this.map?.addControl(this.navControl);
      } else {
        this.map?.removeControl(this.navControl);
        this.navControl = null;
      }
    },
  },
};
</script>

<style>
.mapboxgl-ctrl-geocoder--powered-by,
.mapbox-improve-map {
  display: none !important;
}

.school-popup .mapboxgl-popup-content,
.school-popup .maplibregl-popup-content {
  padding: 4px 2px;
  background: hsla(0, 0%, 100%, 0.7);
}

.mapboxgl-ctrl-top-right,
.maplibregl-ctrl-top-right {
  top: 10px !important;
}
</style>
